import React, { useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Container, FormControlLabel,
  IconButton, LinearProgress, Paper, Stack, Switch, Table, TableBody, TableCell,
  TableContainer, TableHead, TablePagination, TableRow, Tooltip, Typography,
} from '@mui/material';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RefreshIcon from '@mui/icons-material/Refresh';
import { analizarImagenes, procesarImagenes } from '../api/cargaMasivaImagenes';
import { useToast } from '../contexts/ToastContext';

const BATCH_SIZE = 10;
const MAX_FILES = 500;
const MAX_IMAGE_BYTES = 10 * 1024 * 1024;

const statusColors = {
  LISTO: 'success',
  PRODUCTO_NO_ENCONTRADO: 'warning',
  ARCHIVO_INVALIDO: 'error',
  SKU_INVALIDO: 'error',
  DUPLICADO_EN_SELECCION: 'warning',
  YA_TIENE_IMAGEN: 'info',
  ERROR_ANALISIS: 'error',
};

function chunks(items, size = BATCH_SIZE) {
  const result = [];
  for (let index = 0; index < items.length; index += size) result.push(items.slice(index, index + size));
  return result;
}

function analysisSummary(rows) {
  const count = (status) => rows.filter((row) => row.status === status).length;
  return {
    imagesFound: rows.filter((row) => row.decodeValid).length,
    validSkus: rows.filter((row) => Boolean(row.sku) && row.status !== 'SKU_INVALIDO').length,
    productsFound: rows.filter((row) => Boolean(row.product)).length,
    notFound: count('PRODUCTO_NO_ENCONTRADO'),
    alreadyHaveImage: count('YA_TIENE_IMAGEN'),
    duplicates: count('DUPLICADO_EN_SELECCION'),
    errors: rows.filter((row) => ['ARCHIVO_INVALIDO', 'SKU_INVALIDO', 'ERROR_ANALISIS'].includes(row.status)).length,
  };
}

function resultSummary(rows) {
  return {
    processed: rows.length,
    updated: rows.filter((row) => row.result === 'OK').length,
    skipped: rows.filter((row) => row.result === 'OMITIDA').length,
    errors: rows.filter((row) => row.result === 'ERROR').length,
  };
}

function Summary({ values, result = false }) {
  const labels = result
    ? [['processed', 'Procesadas'], ['updated', 'Actualizadas'], ['skipped', 'Omitidas'], ['errors', 'Errores']]
    : [['imagesFound', 'Imágenes encontradas'], ['validSkus', 'SKU válidos'], ['productsFound', 'Productos encontrados'], ['notFound', 'No encontrados'], ['alreadyHaveImage', 'Ya tienen imagen'], ['duplicates', 'Duplicados'], ['errors', 'Errores']];
  return (
    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
      {labels.map(([key, label]) => <Chip key={key} variant="outlined" label={`${label}: ${values?.[key] ?? 0}`} />)}
    </Stack>
  );
}

function Thumb({ src, alt }) {
  if (!src) return <Box sx={{ width: 48, textAlign: 'center', color: 'text.secondary' }}>—</Box>;
  return <Box component="img" src={src} alt={alt} sx={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 1, border: 1, borderColor: 'divider' }} />;
}

export default function CargaMasivaImagenes() {
  const inputRef = useRef(null);
  const nextId = useRef(1);
  const { showToast } = useToast();
  const [selected, setSelected] = useState([]);
  const [overwrite, setOverwrite] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [progress, setProgress] = useState(null);
  const [processed, setProcessed] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const rows = useMemo(() => analysis?.rows || [], [analysis]);
  const visibleRows = useMemo(() => rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage), [rows, page, rowsPerPage]);
  const readyRows = rows.filter((row) => row.status === 'LISTO');
  const failedIds = result?.results.filter((row) => row.result === 'ERROR').map((row) => row.clientId) || [];

  const resetReview = () => {
    setAnalysis(null);
    setResult(null);
    setProcessed(false);
    setPage(0);
  };

  const addFiles = (fileList) => {
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return;
    setSelected((current) => {
      const available = Math.max(0, MAX_FILES - current.length);
      const accepted = incoming.slice(0, available).map((file) => ({ id: nextId.current++, file }));
      if (accepted.length < incoming.length) {
        showToast({ message: `Se permiten como máximo ${MAX_FILES} archivos`, severity: 'warning' });
      }
      return [...current, ...accepted];
    });
    resetReview();
  };

  const removeFile = (id) => {
    setSelected((current) => current.filter((item) => item.id !== id));
    resetReview();
  };

  const clearFiles = () => {
    setSelected([]);
    resetReview();
    if (inputRef.current) inputRef.current.value = '';
  };

  const analyze = async () => {
    if (!selected.length) return showToast({ message: 'Selecciona al menos una imagen', severity: 'warning' });
    setBusy(true);
    setResult(null);
    setProcessed(false);

    const oversized = selected.filter((item) => item.file.size > MAX_IMAGE_BYTES);
    const candidates = selected.filter((item) => item.file.size <= MAX_IMAGE_BYTES);
    const analyzedRows = oversized.map((item) => ({
      clientId: item.id,
      originalName: item.file.name,
      sku: item.file.name.replace(/\.[^.]+$/, ''),
      product: null,
      preview: null,
      decodeValid: false,
      status: 'ARCHIVO_INVALIDO',
      observation: 'La imagen supera el límite de 10 MB',
    }));

    let completed = oversized.length;
    setProgress({ label: 'Analizando imágenes', completed, total: selected.length });
    for (const batch of chunks(candidates)) {
      try {
        const response = await analizarImagenes(batch.map((item) => item.file), overwrite);
        response.data.rows.forEach((row, index) => analyzedRows.push({ ...row, clientId: batch[index].id }));
      } catch (error) {
        const message = error.response?.data?.error || 'No fue posible analizar este lote';
        batch.forEach((item) => analyzedRows.push({
          clientId: item.id,
          originalName: item.file.name,
          sku: item.file.name.replace(/\.[^.]+$/, ''),
          product: null,
          preview: null,
          decodeValid: false,
          status: 'ERROR_ANALISIS',
          observation: message,
        }));
      }
      completed += batch.length;
      setProgress({ label: 'Analizando imágenes', completed, total: selected.length });
    }

    const order = new Map(selected.map((item, index) => [item.id, index]));
    analyzedRows.sort((a, b) => order.get(a.clientId) - order.get(b.clientId));
    const firstBySku = new Map();
    const finalRows = analyzedRows.map((row) => {
      if (['ARCHIVO_INVALIDO', 'SKU_INVALIDO', 'ERROR_ANALISIS'].includes(row.status) || !row.sku) return row;
      if (firstBySku.has(row.sku)) {
        return {
          ...row,
          status: 'DUPLICADO_EN_SELECCION',
          observation: `Se usará ${firstBySku.get(row.sku)} y este archivo será ignorado`,
        };
      }
      firstBySku.set(row.sku, row.originalName);
      return row;
    });

    setAnalysis({ rows: finalRows, summary: analysisSummary(finalRows) });
    setProgress(null);
    setPage(0);
    setBusy(false);
    showToast({ message: `${finalRows.length} archivos analizados`, severity: 'success' });
  };

  const processRows = async (clientIds, retry = false) => {
    const selectedById = new Map(selected.map((item) => [item.id, item]));
    const targets = clientIds.map((id) => selectedById.get(id)).filter(Boolean);
    if (!targets.length) return;
    setBusy(true);

    let resultRows = retry
      ? [...(result?.results || [])]
      : rows.filter((row) => row.status !== 'LISTO').map((row) => ({
          clientId: row.clientId,
          sku: row.sku,
          product: row.product?.nombre || '-',
          result: 'OMITIDA',
          message: row.observation,
        }));
    let completed = 0;
    setProgress({ label: retry ? 'Reintentando imágenes' : 'Procesando imágenes', completed, total: targets.length });

    for (const batch of chunks(targets)) {
      let batchResults;
      try {
        const response = await procesarImagenes(batch.map((item) => item.file), overwrite);
        batchResults = response.data.results.map((row) => ({ ...row, clientId: batch[row.index]?.id }));
      } catch (error) {
        const message = error.response?.data?.error || 'No fue posible procesar este lote';
        batchResults = batch.map((item) => ({
          clientId: item.id,
          sku: rows.find((row) => row.clientId === item.id)?.sku || '-',
          product: rows.find((row) => row.clientId === item.id)?.product?.nombre || '-',
          result: 'ERROR',
          message,
        }));
      }

      const replacements = new Map(batchResults.map((row) => [row.clientId, row]));
      resultRows = resultRows.filter((row) => !replacements.has(row.clientId));
      resultRows.push(...batchResults);
      completed += batch.length;
      setProgress({ label: retry ? 'Reintentando imágenes' : 'Procesando imágenes', completed, total: targets.length });
    }

    const order = new Map(selected.map((item, index) => [item.id, index]));
    resultRows.sort((a, b) => order.get(a.clientId) - order.get(b.clientId));
    setResult({ results: resultRows, summary: resultSummary(resultRows) });
    setProcessed(true);
    setProgress(null);
    setBusy(false);
    const summary = resultSummary(resultRows);
    showToast({ message: `Carga finalizada: ${summary.updated} imágenes actualizadas`, severity: summary.errors ? 'warning' : 'success' });
  };

  return (
    <Box sx={{ height: '100%', overflow: 'auto', p: 2 }}><Container maxWidth="xl">
      <Paper sx={{ p: 3, mb: 2 }}>
        <Typography variant="h5" fontWeight={700}>Carga masiva de imágenes</Typography>
        <Typography color="text.secondary" sx={{ mt: 1 }}>Arrastra las imágenes o selecciónalas. Cada archivo debe llamarse exactamente igual al SKU del producto.</Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>Ejemplos: SE25660BG.jpg · RI1461J6-9G.jpg · KTZ-078R-R.jpg</Typography>
        <Alert severity="info" sx={{ mt: 2 }}>Formatos: JPG, JPEG, PNG y WEBP. Las imágenes se analizan y procesan en lotes de {BATCH_SIZE}.</Alert>

        <Box
          onDragEnter={(event) => { event.preventDefault(); setDragging(true); }}
          onDragOver={(event) => event.preventDefault()}
          onDragLeave={(event) => { event.preventDefault(); if (!event.currentTarget.contains(event.relatedTarget)) setDragging(false); }}
          onDrop={(event) => { event.preventDefault(); setDragging(false); if (!busy) addFiles(event.dataTransfer.files); }}
          onClick={() => !busy && inputRef.current?.click()}
          sx={{
            mt: 3, p: 4, textAlign: 'center', cursor: busy ? 'default' : 'pointer', borderRadius: 2,
            border: '2px dashed', borderColor: dragging ? 'primary.main' : 'divider',
            bgcolor: dragging ? 'action.hover' : 'background.default', transition: '0.2s',
          }}
        >
          <input ref={inputRef} hidden type="file" multiple accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp" onChange={(event) => { addFiles(event.target.files); event.target.value = ''; }} />
          <AddPhotoAlternateIcon color="primary" sx={{ fontSize: 48 }} />
          <Typography fontWeight={700}>Arrastra aquí tus imágenes</Typography>
          <Typography variant="body2" color="text.secondary">o haz clic para seleccionar varias</Typography>
        </Box>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} sx={{ mt: 2 }}>
          <Chip label={`${selected.length} archivos seleccionados`} color={selected.length ? 'primary' : 'default'} />
          <Button variant="outlined" color="error" startIcon={<DeleteOutlineIcon />} disabled={!selected.length || busy} onClick={(event) => { event.stopPropagation(); clearFiles(); }}>Limpiar</Button>
          <Button variant="contained" startIcon={busy ? <CircularProgress size={18} color="inherit" /> : <AddPhotoAlternateIcon />} disabled={!selected.length || busy} onClick={analyze}>Analizar imágenes</Button>
          <Button color="success" variant="contained" startIcon={busy ? <CircularProgress size={18} color="inherit" /> : <PlayArrowIcon />} disabled={!analysis || !readyRows.length || busy || processed} onClick={() => processRows(readyRows.map((row) => row.clientId))}>Procesar imágenes válidas ({readyRows.length})</Button>
          {failedIds.length > 0 && <Button color="warning" variant="contained" startIcon={<RefreshIcon />} disabled={busy} onClick={() => processRows(failedIds, true)}>Reintentar fallidas ({failedIds.length})</Button>}
        </Stack>

        <FormControlLabel sx={{ mt: 2 }} control={<Switch checked={overwrite} disabled={busy} onChange={(event) => { setOverwrite(event.target.checked); resetReview(); }} />} label="Sobrescribir productos que ya tienen imagen" />

        {progress && <Box sx={{ mt: 2 }}>
          <Stack direction="row" justifyContent="space-between"><Typography variant="body2">{progress.label}</Typography><Typography variant="body2">{progress.completed} de {progress.total}</Typography></Stack>
          <LinearProgress variant="determinate" value={progress.total ? (progress.completed / progress.total) * 100 : 0} sx={{ mt: 0.5 }} />
        </Box>}

        {selected.length > 0 && !analysis && <Paper variant="outlined" sx={{ mt: 2, maxHeight: 220, overflow: 'auto' }}>
          {selected.map((item) => <Stack key={item.id} direction="row" alignItems="center" spacing={1} sx={{ px: 2, py: 0.75, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="body2" sx={{ flex: 1 }} noWrap>{item.file.name}</Typography>
            <Typography variant="caption" color="text.secondary">{(item.file.size / 1024 / 1024).toFixed(2)} MB</Typography>
            <Tooltip title="Quitar"><span><IconButton size="small" disabled={busy} onClick={() => removeFile(item.id)}><DeleteOutlineIcon fontSize="small" /></IconButton></span></Tooltip>
          </Stack>)}
        </Paper>}

        {analysis && <Box sx={{ mt: 2 }}><Summary values={analysis.summary} /></Box>}
      </Paper>

      {analysis && <Paper sx={{ mb: 2 }}>
        <TableContainer sx={{ maxHeight: '58vh' }}><Table stickyHeader size="small" sx={{ minWidth: 1150 }}>
          <TableHead><TableRow><TableCell>Miniatura</TableCell><TableCell>Archivo</TableCell><TableCell>SKU detectado</TableCell><TableCell>Producto encontrado</TableCell><TableCell>Imagen actual</TableCell><TableCell>Estado</TableCell><TableCell>Observación</TableCell><TableCell>Quitar</TableCell></TableRow></TableHead>
          <TableBody>{visibleRows.map((row) => <TableRow key={row.clientId} hover>
            <TableCell><Thumb src={row.preview} alt={row.originalName} /></TableCell>
            <TableCell>{row.originalName}</TableCell>
            <TableCell>{row.sku || '—'}</TableCell>
            <TableCell>{row.product?.nombre || '—'}</TableCell>
            <TableCell><Thumb src={row.product?.imagen_url} alt={row.product?.nombre || 'Imagen actual'} /></TableCell>
            <TableCell><Chip size="small" color={statusColors[row.status] || 'default'} label={row.status} /></TableCell>
            <TableCell>{row.observation}</TableCell>
            <TableCell><Tooltip title="Quitar de la selección"><span><IconButton size="small" disabled={busy || processed} onClick={() => removeFile(row.clientId)}><DeleteOutlineIcon fontSize="small" /></IconButton></span></Tooltip></TableCell>
          </TableRow>)}</TableBody>
        </Table></TableContainer>
        <TablePagination component="div" count={rows.length} page={page} rowsPerPage={rowsPerPage} onPageChange={(_, value) => setPage(value)} onRowsPerPageChange={(event) => { setRowsPerPage(Number(event.target.value)); setPage(0); }} rowsPerPageOptions={[10, 25, 50, 100]} labelRowsPerPage="Filas por página" />
      </Paper>}

      {result && <Paper sx={{ p: 2 }}>
        <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>Resultado del procesamiento</Typography>
        <Summary values={result.summary} result />
        <TableContainer sx={{ mt: 2, maxHeight: '55vh' }}><Table stickyHeader size="small">
          <TableHead><TableRow><TableCell>SKU</TableCell><TableCell>Producto</TableCell><TableCell>Resultado</TableCell><TableCell>Mensaje</TableCell></TableRow></TableHead>
          <TableBody>{result.results.map((row) => <TableRow key={row.clientId}><TableCell>{row.sku || '—'}</TableCell><TableCell>{row.product}</TableCell><TableCell><Chip size="small" color={row.result === 'OK' ? 'success' : row.result === 'ERROR' ? 'error' : 'default'} label={row.result} /></TableCell><TableCell>{row.message}</TableCell></TableRow>)}</TableBody>
        </Table></TableContainer>
      </Paper>}
    </Container></Box>
  );
}
